import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useRole } from '@/contexts/RoleContext';
import { UserRole } from '@/types/roles';
import { useClientBotType } from '@/hooks/useClientBotType';

// Import existing pages
import Index from '@/pages/Index';
import Costos from '@/pages/Costos';
import Bots from '@/pages/Bots';
import Reportes from '@/pages/Reportes';
import Configuracion from '@/pages/Configuracion';

// Import new placeholder pages
import Clientes from '@/pages/Clientes';
import Usuarios from '@/pages/Usuarios';
import Leads from '@/pages/Leads';

// Import admin pages
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminClients } from '@/pages/admin/AdminClients';
import { AdminCosts } from '@/pages/admin/AdminCosts';
import { AdminBots } from '@/pages/admin/AdminBots';
import { AdminUsers } from '@/pages/admin/AdminUsers';
import { AdminReports } from '@/pages/admin/AdminReports';
import { AdminSettings } from '@/pages/admin/AdminSettings';

// Import developer pages
import { DeveloperDashboard } from '@/pages/developer/DeveloperDashboard';
import { DeveloperBots } from '@/pages/developer/DeveloperBots';
import { DeveloperCosts } from '@/pages/developer/DeveloperCosts';
import { DeveloperSettings } from '@/pages/developer/DeveloperSettings';

// Import client pages
import { ClientDashboard } from '@/pages/client/ClientDashboard';
import { ClientBot } from '@/pages/client/ClientBot';
import { ClientAssistant } from '@/pages/client/ClientAssistant';
import { ClientReports } from '@/pages/client/ClientReports';
import { ClientProfile } from '@/pages/client/ClientProfile';
import { ClientTraces } from '@/pages/client/ClientTraces';
import { ClientThemeProvider } from '@/components/client/shared/ClientThemeProvider';

// Import shared pages
import { Profile } from '@/pages/Profile';

// Component mapping by path and role
const componentMap: Record<string, React.ComponentType | Record<UserRole, React.ComponentType>> = {
  '/dashboard': {
    [UserRole.ADMIN]: AdminDashboard,
    [UserRole.DEVELOPER]: DeveloperDashboard,
    [UserRole.CLIENT]: ClientDashboard,
  },
  '/clientes': {
    [UserRole.ADMIN]: AdminClients,
    [UserRole.DEVELOPER]: Clientes,
    [UserRole.CLIENT]: Clientes,
  },
  '/leads': {
    [UserRole.ADMIN]: AdminClients,
    [UserRole.DEVELOPER]: Clientes,
    [UserRole.CLIENT]: Leads,
  },
  '/traces': {
    [UserRole.ADMIN]: AdminClients,
    [UserRole.DEVELOPER]: Clientes,
    [UserRole.CLIENT]: ClientTraces,
  },
  '/costos': {
    [UserRole.ADMIN]: AdminCosts,
    [UserRole.DEVELOPER]: DeveloperCosts,
    [UserRole.CLIENT]: Costos,
  },
  '/bots': {
    [UserRole.ADMIN]: AdminBots,
    [UserRole.DEVELOPER]: DeveloperBots,
    [UserRole.CLIENT]: ClientBot,
  },
  '/assistant': {
    [UserRole.ADMIN]: AdminBots,
    [UserRole.DEVELOPER]: DeveloperBots,
    [UserRole.CLIENT]: ClientAssistant,
  },
  '/usuarios': {
    [UserRole.ADMIN]: AdminUsers,
    [UserRole.DEVELOPER]: Usuarios,
    [UserRole.CLIENT]: Usuarios,
  },
  '/reportes': {
    [UserRole.ADMIN]: AdminReports,
    [UserRole.DEVELOPER]: Reportes,
  },
  '/configuracion': {
    [UserRole.ADMIN]: AdminSettings,
    [UserRole.DEVELOPER]: DeveloperSettings,
  },
  '/profile': {
    [UserRole.ADMIN]: Profile,
    [UserRole.DEVELOPER]: Profile,
    [UserRole.CLIENT]: Profile,
  },
};

export const RoleBasedRoutes: React.FC = () => {
  const { currentRole } = useRole();
  const location = useLocation();
  const { botType, isLoading: botTypeLoading } = useClientBotType();

  // Handle conditional routing for CLIENT role based on bot type
  if (currentRole === UserRole.CLIENT) {
    // Show loading while determining bot type for any navigation-sensitive routes
    if (botTypeLoading && (location.pathname === '/leads' || location.pathname === '/traces' || location.pathname === '/dashboard')) {
      return (
        <ClientThemeProvider>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p>Cargando información del cliente...</p>
            </div>
          </div>
        </ClientThemeProvider>
      );
    }
    
    // Handle redirects based on bot type (only after loading is complete)
    if (!botTypeLoading) {
      if (location.pathname === '/leads' && botType === 'voicebot') {
        return <Navigate to="/traces" replace />;
      }
      if (location.pathname === '/traces' && botType === 'chatbot') {
        return <Navigate to="/leads" replace />;
      }
    }
  }

  // Get the component for the current path
  const mapping = componentMap[location.pathname];
  const Component = typeof mapping === 'object' && mapping !== null
    ? (mapping as Record<UserRole, React.ComponentType>)[currentRole] 
    : mapping as React.ComponentType;

  // If no component found, redirect to dashboard
  if (!Component) {
    return <Navigate to="/dashboard" replace />;
  }

  // Wrap client components with theme provider
  if (currentRole === UserRole.CLIENT) {
    return (
      <ClientThemeProvider>
        <Component />
      </ClientThemeProvider>
    );
  }

  return <Component />;
};